import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, AlertCircle, Loader2, Phone, Mail, MapPin } from 'lucide-react';
import axios from 'axios';
import Seo from '@/components/Seo';
import { Reveal, Magnetic } from '@/components/motion';
import { CONTACT, INTEREST_OPTIONS } from '@/content/site';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTEREST_BY_PARAM = Object.fromEntries(INTEREST_OPTIONS.map((o) => [o.value, o.value]));

export default function Contact() {
  const [params] = useSearchParams();
  const interestParam = INTEREST_BY_PARAM[params.get('interest') || ''] ?? '';

  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    interest: interestParam,
    message: '',
    website: '',
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [serverError, setServerError] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Please enter your name.';
    if (!form.company.trim()) errs.company = 'Please enter your company.';
    if (!form.email.trim()) errs.email = 'Please enter your work email.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      errs.email = 'Please enter a valid email address.';
    if (!form.message.trim()) errs.message = 'Please tell us briefly about your challenge.';
    return errs;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus('sending');
    setServerError('');
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setServerError(
        err?.response?.data?.detail ||
          'Something went wrong sending your request. Please try again, or call us directly.'
      );
    }
  };

  return (
    <>
      <Seo
        title="Schedule a Consultation | KTK Advisors"
        description="Tell KTK Advisors about your business and the area you'd like to improve. The first conversation is a practical, clear consultation."
        path="/contact"
      />

      <section className="relative overflow-hidden bg-deep" data-testid="contact-hero">
        <div className="aura aura--gold" style={{ width: 560, height: 560, top: -200, right: -140 }} />
        <div className="wrap pt-40 pb-20 lg:pt-48 lg:pb-24 relative">
          <Reveal>
            <p className="eyebrow-mono">Contact</p>
            <h1 className="h1 mt-7 max-w-[24ch]">
              Let's talk about what your business could <span className="gold-italic">do better.</span>
            </h1>
            <p className="lede mt-8 max-w-[56ch]">
              Send a note about where you suspect there's room to improve—or
              simply call. The first conversation is a consultation.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-deep section" data-testid="contact-main">
        <div className="wrap grid lg:grid-cols-[1fr_1.4fr] gap-14 items-start">
          <Reveal>
            <div>
              <p className="eyebrow-mono">Direct Contact</p>
              <div className="mt-9 space-y-4">
                <a href={CONTACT.phoneHref} data-testid="contact-phone-link" className="glass glass-hover flex items-center gap-5 p-6 block">
                  <span className="w-11 h-11 flex-none rounded-full border border-gold/40 flex items-center justify-center">
                    <Phone size={16} className="text-goldbright" />
                  </span>
                  <span>
                    <span className="block font-mono text-[9.5px] uppercase tracking-[0.24em] text-mist">Phone</span>
                    <span className="block text-[17px] font-semibold text-ivory mt-1">{CONTACT.phone}</span>
                  </span>
                </a>
                <a href={CONTACT.emailHref} data-testid="contact-email-link" className="glass glass-hover flex items-center gap-5 p-6 block">
                  <span className="w-11 h-11 flex-none rounded-full border border-gold/40 flex items-center justify-center">
                    <Mail size={16} className="text-goldbright" />
                  </span>
                  <span>
                    <span className="block font-mono text-[9.5px] uppercase tracking-[0.24em] text-mist">Email</span>
                    <span className="block text-[17px] font-semibold text-ivory mt-1 break-all">{CONTACT.email}</span>
                  </span>
                </a>
                <div className="glass flex items-center gap-5 p-6">
                  <span className="w-11 h-11 flex-none rounded-full border border-gold/40 flex items-center justify-center">
                    <MapPin size={16} className="text-goldbright" />
                  </span>
                  <span>
                    <span className="block font-mono text-[9.5px] uppercase tracking-[0.24em] text-mist">Address</span>
                    <span className="block text-[15.5px] text-ivory leading-relaxed mt-1">
                      {CONTACT.address1}
                      <br />
                      {CONTACT.address2}
                    </span>
                  </span>
                </div>
              </div>
              <div
                className="mt-8 p-6 rounded-2xl max-w-md"
                style={{ border: '1px solid rgba(212,175,55,0.4)', background: 'rgba(178,139,67,0.08)' }}
              >
                <p className="text-[13.5px] leading-relaxed text-ivory/90">
                  Please do not include patient information, financial account
                  details, or other sensitive records.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            {status === 'success' ? (
              <div data-testid="contact-success-panel" className="glass p-12 text-center glow-gold">
                <CheckCircle2 size={46} className="text-goldbright mx-auto" />
                <h2 className="h2 mt-7">Request received.</h2>
                <p className="body-copy text-mist mt-5 max-w-[44ch] mx-auto">
                  Your request has been sent to KTK Advisors. You can also reach
                  us directly at{' '}
                  <a href={CONTACT.phoneHref} data-testid="contact-success-phone-link" className="font-semibold text-goldbright">
                    {CONTACT.phone}
                  </a>
                  .
                </p>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={onSubmit} noValidate className="glass p-8 lg:p-12 glow-gold">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className={'field' + (errors.name ? ' field--error' : '')}>
                    <label htmlFor="cf-name">Name</label>
                    <input
                      id="cf-name"
                      data-testid="contact-name-input"
                      value={form.name}
                      onChange={set('name')}
                      placeholder="Your name"
                      autoComplete="name"
                    />
                    {errors.name && <p className="field-error-msg" data-testid="contact-name-error">{errors.name}</p>}
                  </div>
                  <div className={'field' + (errors.company ? ' field--error' : '')}>
                    <label htmlFor="cf-company">Company</label>
                    <input
                      id="cf-company"
                      data-testid="contact-company-input"
                      value={form.company}
                      onChange={set('company')}
                      placeholder="Company name"
                      autoComplete="organization"
                    />
                    {errors.company && <p className="field-error-msg" data-testid="contact-company-error">{errors.company}</p>}
                  </div>
                  <div className={'field' + (errors.email ? ' field--error' : '')}>
                    <label htmlFor="cf-email">Work email</label>
                    <input
                      id="cf-email"
                      data-testid="contact-email-input"
                      type="email"
                      value={form.email}
                      onChange={set('email')}
                      placeholder="you@company.com"
                      autoComplete="email"
                    />
                    {errors.email && <p className="field-error-msg" data-testid="contact-email-error">{errors.email}</p>}
                  </div>
                  <div className="field">
                    <label htmlFor="cf-phone">Phone (optional)</label>
                    <input
                      id="cf-phone"
                      data-testid="contact-phone-input"
                      type="tel"
                      value={form.phone}
                      onChange={set('phone')}
                      placeholder="(555) 000-0000"
                      autoComplete="tel"
                    />
                  </div>
                  <div className="field sm:col-span-2">
                    <label htmlFor="cf-interest">Area of interest</label>
                    <select
                      id="cf-interest"
                      data-testid="contact-interest-select"
                      value={form.interest}
                      onChange={set('interest')}
                    >
                      {INTEREST_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={'field sm:col-span-2' + (errors.message ? ' field--error' : '')}>
                    <label htmlFor="cf-message">Brief description of the challenge</label>
                    <textarea
                      id="cf-message"
                      data-testid="contact-message-input"
                      rows={5}
                      value={form.message}
                      onChange={set('message')}
                      placeholder="A few sentences about where you'd like to improve—revenue collection, benefits, technology costs, shipping, marketing, or something else."
                    />
                    {errors.message && <p className="field-error-msg" data-testid="contact-message-error">{errors.message}</p>}
                  </div>
                </div>

                {/* Honeypot — hidden from humans */}
                <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
                  <label htmlFor="cf-website">Website</label>
                  <input
                    id="cf-website"
                    data-testid="contact-website-honeypot"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={set('website')}
                  />
                </div>

                {status === 'error' && (
                  <div
                    data-testid="contact-error-alert"
                    className="mt-6 flex items-start gap-3 p-4 rounded-xl"
                    style={{ border: '1px solid rgba(208,106,85,0.6)', background: 'rgba(208,106,85,0.12)' }}
                    role="alert"
                  >
                    <AlertCircle size={18} className="text-[#e29682] mt-0.5 flex-none" />
                    <p className="text-[14px] text-[#e29682] leading-relaxed">{serverError}</p>
                  </div>
                )}

                <Magnetic className="block mt-9">
                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    className="btn btn-gold w-full"
                    disabled={status === 'sending'}
                  >
                    {status === 'sending' ? (
                      <>
                        <Loader2 size={17} className="animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Send Request
                        <ArrowRight size={17} />
                      </>
                    )}
                  </button>
                </Magnetic>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
